import type { Role } from './domain.js';

/**
 * Permissions applicatives (conception 3.1).
 *
 * La matrice est definie ici une seule fois : l'API s'en sert pour ses gardes,
 * l'interface pour afficher ou masquer une action. Un role se lit toujours a
 * travers ses permissions, jamais par comparaison directe (`role === 'ADMIN'`) :
 * ajouter un role ne demande alors qu'une ligne dans cette table.
 */
export const PERMISSIONS = [
  /** Voir tous les leads ; sinon les siens et le vivier. */
  'leads.voir_tous',
  /** Modifier tout lead visible, sans se l'attribuer. */
  'leads.modifier_tous',
  /** Modifier les leads de son perimetre (les siens et le vivier). */
  'leads.ecrire',
  'leads.creer',
  /** Attribuer un lead a n'importe qui, ou le rendre au vivier. */
  'leads.attribuer',
  /** S'attribuer un lead du vivier. */
  'leads.prendre',
  'leads.supprimer',
  'leads.exporter',
  /** Trouver des leads (imports SIRENE) et enrichir en lot. */
  'leads.importer',
  'ia.generer',
  /** Offre commerciale et consommation de l'IA. */
  'ia.configurer',
  'equipe.gerer',
  /** Etapes (statuts) du pipeline. */
  'pipeline.configurer',
  /** Jetons d'API. */
  'jetons.gerer',
  /** Statistiques de toute l'equipe. */
  'stats.equipe',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const PERMISSIONS_PAR_ROLE: Record<Role, readonly Permission[]> = {
  // L'administrateur a toutes les permissions, y compris les futures.
  ADMIN: PERMISSIONS,
  MANAGER: [
    'leads.voir_tous',
    'leads.modifier_tous',
    'leads.ecrire',
    'leads.creer',
    'leads.attribuer',
    'leads.prendre',
    'leads.exporter',
    'leads.importer',
    'ia.generer',
    'stats.equipe',
  ],
  COMMERCIAL: ['leads.ecrire', 'leads.creer', 'leads.prendre', 'ia.generer'],
  LECTEUR: ['leads.voir_tous', 'stats.equipe'],
};

/**
 * Permissions d'un role. Un role inconnu (client plus recent que l'API, par
 * exemple) n'en a aucune : le defaut est le refus.
 */
export function permissionsDuRole(role: Role): readonly Permission[] {
  return PERMISSIONS_PAR_ROLE[role] ?? [];
}

export function aPermission(role: Role, permission: Permission): boolean {
  return permissionsDuRole(role).includes(permission);
}
